import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { ALARM_SOUNDS_MAP, DEFAULT_ALARM_SOUND, resolveAlarmSound } from '@utils/sounds';

const getInitialAudio = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('selectedSound');
      // The stored value used to be trusted as-is, which is how 89 commerces ended up with a
      // permanently mute order alarm (PLICK-FRONT-PANEL-COMERCIO-64): their preference points at a
      // 2023 asset path that CloudFront now answers with the SPA's index.html.
      const resolved = resolveAlarmSound(stored);
      // Repaired in place, not just in memory: leaving the stale value behind would mute the alarm
      // again on the next reload and keep the Ajustes selector with no tone marked as selected
      // (AjustesV2 compares `s.src === selectedSrc`, so a stale value highlights nothing).
      if (stored !== resolved) {
        try {
          localStorage.setItem('selectedSound', resolved);
        } catch {
          // The on-disk repair is best-effort and MUST NOT cost the tone we just recovered: a full
          // quota (or a browser that throws on write) would otherwise drop `resolved` on the floor
          // and start the commerce on the default tone — changing the tone of exactly the commerce
          // this function exists to give its tone back to, on every reload while the quota is full.
        }
      }
      return resolved;
    }
  } catch {
    // LocalStorage not reachable at all (tests, or a browser that blocks site data): reading
    // `window.localStorage` itself throws, so there is nothing to resolve and the default stands.
  }
  return DEFAULT_ALARM_SOUND;
};

const initialState = {
  audio: getInitialAudio(),
};

export const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    setAudio: (state, action: PayloadAction<keyof typeof ALARM_SOUNDS_MAP>) => {
      return { ...state, audio: action.payload };
    },
    reset: () => initialState,
  },
});
